import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, Check, ArrowRight } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { vehicleImage, FLEET_FALLBACK } from "@/lib/vehicleImages";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BookingSearch } from "@/components/BookingSearch";
import { Button } from "@/components/ui/button";

export default function Results() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(false);

  const from = params.get("from");
  const to = params.get("to");
  const pax = Number(params.get("pax") || 2);
  const rt = params.get("rt") === "1";

  useEffect(() => {
    if (!from || !to) {
      navigate("/");
      return;
    }
    // Guard direct URLs: a transfer must have an airport on one end.
    const AIRPORTS = ["ayt", "gzp"];
    if (!AIRPORTS.includes(from) && !AIRPORTS.includes(to)) {
      setLoading(false);
      setError("airport");
      setEditing(true);
      return;
    }
    setError(false);
    setLoading(true);
    api
      .post("/quote", { origin_id: from, destination_id: to, pax, round_trip: rt })
      .then(({ data }) => setQuote(data))
      .catch((e) => {
        setError("load");
        toast.error(apiError(e));
      })
      .finally(() => setLoading(false));
  }, [from, to, pax, rt, navigate]);

  const choose = (opt) => {
    const q = new URLSearchParams(params);
    q.set("class", opt.class_id);
    navigate(`/checkout?${q.toString()}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("results.route")}</p>
            <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="results-title">
              {t("results.title")}
            </h1>
            {quote && (
              <p className="mt-3 text-sm text-slate-600" data-testid="results-route-summary">
                {quote.origin.name} <ArrowRight size={13} className="inline" /> {quote.destination.name} ·{" "}
                {quote.distance_km} {t("results.km")} · {quote.duration_min} {t("results.min")} · {pax}{" "}
                {t("results.pax")}
                {rt ? ` · ${t("search.round")}` : ""}
              </p>
            )}
          </div>
          <Button
            data-testid="edit-search-btn"
            variant="outline"
            onClick={() => setEditing((v) => !v)}
          >
            {t("results.edit")}
          </Button>
        </div>

        {editing && (
          <div className="mt-8">
            <BookingSearch
              initial={{
                from,
                to,
                pax,
                rt: rt ? "1" : "0",
                date: params.get("date"),
                time: params.get("time"),
                ret: params.get("ret"),
              }}
            />
          </div>
        )}

        {loading && <p className="mt-16 text-slate-500">{t("common.loading")}</p>}

        {!loading && error && (
          <div className="mt-12 rounded-xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="results-error">
            <p className="text-sm text-amber-800">
              {error === "airport" ? t("search.errAirport") : t("results.loadError")}
            </p>
            <Button className="mt-4 bg-sea hover:bg-sea-dark" data-testid="results-error-edit-btn" onClick={() => setEditing(true)}>
              {t("results.edit")}
            </Button>
          </div>
        )}

        {!loading && !error && quote && (
          <div className="mt-10 space-y-4" data-testid="vehicle-options">
            {(quote.options || []).length === 0 && <p className="text-slate-500">{t("results.empty")}</p>}
            {(quote.options || []).map((o, i) => (
              <article
                key={o.class_id}
                data-testid={`vehicle-option-${o.class_id}`}
                className="grid gap-6 rounded-xl border border-slate-200 bg-white p-5 transition-transform duration-300 hover:-translate-y-0.5 ambient md:grid-cols-12 md:p-6 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="h-40 overflow-hidden rounded-lg bg-slate-100 md:col-span-3 md:h-32">
                  <img
                    src={vehicleImage(o.class_id, o.image)}
                    alt={o.name}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FLEET_FALLBACK; }}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="md:col-span-6">
                  <h3 className="font-display text-xl">{o.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{o.tagline}</p>
                  <div className="mt-3 flex gap-5 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Users size={14} /> {o.capacity} {t("results.pax")}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase size={14} /> {o.luggage} {t("results.bags")}
                    </span>
                  </div>
                  <ul className="mt-4 grid gap-1.5 sm:grid-cols-2">
                    {(o.features || []).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                        <Check size={13} className="text-sea" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col items-start justify-between gap-4 border-t border-slate-100 pt-4 md:col-span-3 md:items-end md:border-l md:border-t-0 md:pl-6 md:pt-0">
                  <div className="md:text-right">
                    <p className="font-display text-3xl text-slate-900" data-testid={`price-${o.class_id}`}>
                      {eur(o.price)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {o.per_person && o.class_id !== "shuttle" ? t("results.perPerson") : t("results.total")}
                    </p>
                  </div>
                  <Button
                    data-testid={`select-${o.class_id}-btn`}
                    disabled={!o.available}
                    onClick={() => choose(o)}
                    className="w-full bg-sea transition-colors duration-200 hover:bg-sea-dark md:w-auto"
                  >
                    {o.available ? t("results.select") : t("results.unavailable")}
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
